import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { BarChart3, Circle, CircleDot, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = {
  todo: 12,
  in_progress: 8,
  done: 25,
  total: 45,
  percentage: 56,
};

export function TaskProgressTile({ filter, useMockData }) {
  const { company } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  // Observe container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        let taskQuery = supabase
          .from('tasks')
          .select('subtasks, projects!inner(company_id)')
          .eq('projects.company_id', company.id);

        if (filter?.spaceId) {
          taskQuery = taskQuery.eq('project_id', filter.spaceId);
        }

        const { data: tasks, error } = await taskQuery;
        if (error) throw error;

        // Calculate progress from subtasks
        // - todo: no subtasks OR all subtasks not started
        // - in_progress: some subtasks completed but not all
        // - done: all subtasks completed
        const counts = {
          todo: 0,
          in_progress: 0,
          done: 0,
        };

        (tasks || []).forEach(task => {
          const subtasks = task.subtasks || [];

          if (subtasks.length === 0) {
            // No subtasks = todo
            counts.todo++;
          } else {
            const completed = subtasks.filter(st => st.completed).length;
            const total = subtasks.length;

            if (completed === 0) {
              counts.todo++;
            } else if (completed === total) {
              counts.done++;
            } else {
              counts.in_progress++;
            }
          }
        });

        const total = counts.todo + counts.in_progress + counts.done;
        const percentage = total > 0 ? Math.round((counts.done / total) * 100) : 0;

        setData({
          ...counts,
          total,
          percentage,
        });
      } catch (err) {
        console.error('Failed to load task progress:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, filter?.spaceId, useMockData]);

  // Determine layout based on dimensions
  const { width, height } = dimensions;
  const isXS = width < 150 || height < 100;
  const isSmall = width < 200 || height < 150;
  const isLarge = width >= 300 && height >= 280;

  if (loading) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full">
        <div className="animate-pulse flex gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="text-center">
              <div className="w-12 h-16 bg-gray-200 rounded mb-2" />
              <div className="h-2 w-10 bg-gray-200 rounded mx-auto" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center h-full text-gray-400">
        <BarChart3 className="h-8 w-8 mb-2" />
        <span className="text-sm">No tasks found</span>
      </div>
    );
  }

  const columns = [
    {
      key: 'todo',
      label: 'To Do',
      shortLabel: 'To Do',
      count: data.todo,
      icon: Circle,
      color: 'text-gray-500',
      bgColor: 'bg-gray-100',
      barColor: 'bg-gray-400',
    },
    {
      key: 'in_progress',
      label: 'In Progress',
      shortLabel: 'Active',
      count: data.in_progress,
      icon: CircleDot,
      color: 'text-blue-500',
      bgColor: 'bg-blue-100',
      barColor: 'bg-blue-500',
    },
    {
      key: 'done',
      label: 'Done',
      shortLabel: 'Done',
      count: data.done,
      icon: CheckCircle,
      color: 'text-green-500',
      bgColor: 'bg-green-100',
      barColor: 'bg-green-500',
    },
  ];

  // XS Layout: Just percentage
  if (isXS) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full overflow-visible">
        <div className="text-center">
          <div className={cn(
            'text-3xl font-bold leading-none',
            data.percentage >= 80 ? 'text-green-600' :
            data.percentage >= 50 ? 'text-blue-600' : 'text-gray-700'
          )}>
            {data.percentage}%
          </div>
          <p className="text-xs text-gray-500 mt-1">done</p>
        </div>
      </div>
    );
  }

  // Small Layout: Mini bars with percentage
  if (isSmall) {
    return (
      <div ref={containerRef} className="flex flex-col h-full p-2 overflow-visible">
        {/* Percentage header */}
        <div className="text-center mb-2">
          <span className={cn(
            'text-2xl font-bold',
            data.percentage >= 80 ? 'text-green-600' :
            data.percentage >= 50 ? 'text-blue-600' : 'text-gray-700'
          )}>
            {data.percentage}%
          </span>
          <p className="text-[10px] text-gray-500">complete</p>
        </div>

        {/* Mini progress bar */}
        <div className="flex h-2 rounded-full overflow-hidden bg-gray-100 mt-auto">
          <div
            className="bg-green-500 transition-all"
            style={{ width: `${(data.done / data.total) * 100}%` }}
          />
          <div
            className="bg-blue-500 transition-all"
            style={{ width: `${(data.in_progress / data.total) * 100}%` }}
          />
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-gray-500">{data.done} done</span>
          <span className="text-[10px] text-gray-500">{data.total} total</span>
        </div>
      </div>
    );
  }

  // Large Layout: Full kanban-style columns
  if (isLarge) {
    return (
      <div ref={containerRef} className="h-full flex flex-col p-2">
        {/* Kanban columns */}
        <div className="flex-1 flex gap-3 min-h-0">
          {columns.map((col) => {
            const Icon = col.icon;
            const heightPct = data.total > 0 ? (col.count / data.total) * 100 : 0;

            return (
              <div key={col.key} className="flex-1 flex flex-col items-center">
                <div
                  className={cn(
                    "w-full rounded-lg flex flex-col items-center justify-end p-3",
                    col.bgColor,
                    "min-h-[100px]"
                  )}
                  style={{ height: `${Math.max(heightPct, 30)}%` }}
                >
                  <Icon className={cn("h-5 w-5 mb-1", col.color)} />
                  <span className="text-2xl font-bold">{col.count}</span>
                </div>
                <p className="text-xs text-gray-600 mt-2 text-center">{col.label}</p>
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="mt-3 pt-3 border-t">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Completion</span>
            <span className="text-sm font-medium">{data.percentage}%</span>
          </div>
          <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
            <div
              className="bg-green-500 transition-all"
              style={{ width: `${(data.done / data.total) * 100}%` }}
            />
            <div
              className="bg-blue-500 transition-all"
              style={{ width: `${(data.in_progress / data.total) * 100}%` }}
            />
          </div>
          <p className="text-xs text-center text-gray-500 mt-2">
            {data.total} total tasks
          </p>
        </div>
      </div>
    );
  }

  // Default/Medium Layout: Horizontal stats with progress bar
  return (
    <div ref={containerRef} className="h-full flex flex-col p-2">
      {/* Stats row */}
      <div className="flex gap-2 mb-2">
        {columns.map((col) => {
          const Icon = col.icon;
          return (
            <div key={col.key} className="flex-1 text-center">
              <div className={cn("p-2 rounded-lg", col.bgColor)}>
                <Icon className={cn("h-4 w-4 mx-auto mb-0.5", col.color)} />
                <span className="text-lg font-bold block">{col.count}</span>
              </div>
              <p className="text-[10px] text-gray-600 mt-1">{col.shortLabel}</p>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="mt-auto">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-600">Done</span>
          <span className="text-xs font-medium">{data.percentage}%</span>
        </div>
        <div className="flex h-1.5 rounded-full overflow-hidden bg-gray-100">
          <div
            className="bg-green-500 transition-all"
            style={{ width: `${(data.done / data.total) * 100}%` }}
          />
          <div
            className="bg-blue-500 transition-all"
            style={{ width: `${(data.in_progress / data.total) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}
